const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface MonthOption { key: string; label: string }

/** Current IST month as year + 0-based month. */
function istYearMonth(now: Date): { y: number; m: number } {
  const ist = new Date(now.getTime() + 5.5 * 3600 * 1000);
  return { y: ist.getUTCFullYear(), m: ist.getUTCMonth() };
}

export const monthKey = (y: number, m0: number) => `${y}-${String(m0 + 1).padStart(2, '0')}`;

/** `count` months, newest (this IST month) first. Label = `Nov 2026`. */
export function monthOptions(count = 24, now: Date = new Date(), future = 0): MonthOption[] {
  const { y, m } = istYearMonth(now);
  const out: MonthOption[] = [];
  for (let i = -future; i < count; i++) {
    const idx = y * 12 + m - i;
    const yy = Math.floor(idx / 12);
    const mm = idx - yy * 12;
    out.push({ key: monthKey(yy, mm), label: `${MON[mm]} ${yy}` });
  }
  return out;
}

export const currentMonthKey = (now: Date = new Date()) => monthOptions(1, now)[0].key;

/** `2026-11` → `Nov 2026`. */
export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return `${MON[(m || 1) - 1]} ${y}`;
}

export interface FiscalOfMonth {
  fiscalYear: string;
  periodIndex: number;
  quarterIndex: number;
  /** the month has begun (IST) */
  isStarted: boolean;
  /** the month ended before today (IST) */
  isClosed: boolean;
}

/** Fiscal facts for `YYYY-MM` (FY starts April: Apr = 1 … Mar = 12). */
export function fiscalOfMonth(key: string, now: Date = new Date()): FiscalOfMonth {
  const [y, m] = key.split('-').map(Number);
  const m0 = m - 1;
  const fyStart = m0 >= 3 ? y : y - 1;
  const periodIndex = ((m0 - 3 + 12) % 12) + 1;
  const cur = istYearMonth(now);
  const diff = y * 12 + m0 - (cur.y * 12 + cur.m);
  return {
    fiscalYear: `${fyStart}-${String((fyStart + 1) % 100).padStart(2, '0')}`,
    periodIndex,
    quarterIndex: Math.floor((periodIndex - 1) / 3) + 1,
    isStarted: diff <= 0,
    isClosed: diff < 0,
  };
}

const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** `2026-11` → `November`. */
export const monthName = (key: string): string => MONTH_LONG[(Number(key.split('-')[1]) || 1) - 1];

/** First/last day (YYYY-MM-DD) of a month key; the current month ends today (IST). */
export function monthRange(key: string, now: Date = new Date()): { from: string; to: string } {
  const [y, m] = key.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  const ist = new Date(now.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  const to = key === currentMonthKey(now) ? ist : last;
  return { from: `${key}-01`, to };
}

/** `/sales-targets?month=2026-11` — the Targets screen opened on a given (default: this) month. */
export const salesTargetsLink = (now: Date = new Date()): string => `/sales-targets?month=${currentMonthKey(now)}`;
